import { useState, type FormEvent } from "react";
import { Alert, Button, InputText, Typography } from "@/deps/ui-library";
import { SIGNUP_PATH, useSignupOpen } from "./SignupPage";

/**
 * The credential boundary of the product (ADR-0003). The page posts what the person typed to the
 * API's own `/bff/login`; the API checks it with the platform identity over a service call and
 * answers with a cookie. No token is ever in this page's hands, and nothing here is scripted beyond
 * the form itself.
 */
type Step =
  | { kind: "password" }
  | { kind: "totp"; userId: string }
  | { kind: "forgot" }
  | { kind: "sent" };

interface LoginOutcome {
  mfaRequired: boolean;
  userId: string;
}

interface Problem {
  title?: string;
  code?: string;
}

async function post(path: string, body: unknown): Promise<Response> {
  return fetch(path, {
    method: "POST",
    credentials: "same-origin",
    headers: { "Content-Type": "application/json", Accept: "application/json" },
    body: JSON.stringify(body),
  });
}

async function problemTitle(response: Response, fallback: string): Promise<string> {
  try {
    const problem = (await response.json()) as Problem;
    return problem.title ?? fallback;
  } catch {
    return fallback;
  }
}

export function LoginPage({ onSignedIn }: { onSignedIn: () => void }) {
  const [step, setStep] = useState<Step>({ kind: "password" });
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const signupOpen = useSignupOpen();

  async function submitPassword(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const response = await post("/bff/login", { login, password });
      if (response.status === 202) {
        const outcome = (await response.json()) as LoginOutcome;
        setStep({ kind: "totp", userId: outcome.userId });
        return;
      }
      if (!response.ok) {
        setError(await problemTitle(response, "Sign-in failed."));
        return;
      }
      onSignedIn();
    } finally {
      setBusy(false);
      setPassword("");
    }
  }

  async function submitForgot(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      // The answer is the same whether or not there's an account. Only a failure to reach us says otherwise.
      const response = await post("/bff/forgot-password", { login });
      if (!response.ok) {
        setError("That could not be sent. Try again shortly.");
        return;
      }
      setStep({ kind: "sent" });
    } finally {
      setBusy(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== "totp") return;
    setBusy(true);
    setError(null);
    try {
      const response = await post("/bff/totp", { userId: step.userId, code });
      if (!response.ok) {
        setError(await problemTitle(response, "That code was not accepted."));
        return;
      }
      onSignedIn();
    } finally {
      setBusy(false);
      setCode("");
    }
  }

  return (
    <main className="bg-canvas grid min-h-screen lg:grid-cols-[440px_1fr]">
      <div className="bg-card flex flex-col justify-center gap-5 px-8 py-10">
        <div className="flex flex-col gap-1">
          <h1 className="text-fg m-0 text-xl font-semibold">
            {step.kind === "forgot" || step.kind === "sent"
              ? "Reset your password"
              : "Sign in to Rigo Accounting"}
          </h1>
          <Typography.Text as="p" variant="small" muted className="m-0">
            One account across every Rigo product.
          </Typography.Text>
        </div>
        {error && (
          <Alert
            config={{ variant: "subtle", colorScheme: "danger", size: "sm" }}
            description={error}
          />
        )}
        {step.kind === "password" ? (
          <form onSubmit={submitPassword} className="flex flex-col gap-3">
            <InputText
              label="Username or email"
              name="login"
              autoComplete="username"
              required
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
            <InputText
              label="Password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
            <Button colorScheme="primary" type="submit" disabled={busy}>
              Sign in
            </Button>
            <Button
              type="button"
              variant="link"
              colorScheme="primary"
              size="sm"
              className="self-start"
              onClick={() => {
                setError(null);
                setStep({ kind: "forgot" });
              }}
            >
              Forgot password?
            </Button>
          </form>
        ) : null}
        {step.kind === "totp" ? (
          <form onSubmit={submitCode} className="flex flex-col gap-3">
            <Typography.Text as="p" variant="small" muted className="m-0">
              Enter the six-digit code from your authenticator app.
            </Typography.Text>
            <InputText
              label="Authenticator code"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="tracking-widest"
            />
            <Button colorScheme="primary" type="submit" disabled={busy}>
              Continue
            </Button>
          </form>
        ) : null}
        {step.kind === "forgot" ? (
          <form onSubmit={submitForgot} className="flex flex-col gap-3">
            <Typography.Text as="p" variant="small" muted className="m-0">
              We'll send a link to the address on your account.
            </Typography.Text>
            <InputText
              label="Username or email"
              name="login"
              autoComplete="username"
              required
              value={login}
              onChange={(e) => setLogin(e.target.value)}
            />
            <Button colorScheme="primary" type="submit" disabled={busy}>
              Send the link
            </Button>
            <Button
              type="button"
              variant="link"
              colorScheme="primary"
              size="sm"
              className="self-start"
              onClick={() => setStep({ kind: "password" })}
            >
              Back to sign in
            </Button>
          </form>
        ) : null}
        {step.kind === "password" && signupOpen ? (
          <Typography.Text as="p" variant="small" muted className="m-0">
            New to Rigo?{" "}
            <a href={SIGNUP_PATH} className="text-primary font-medium no-underline hover:underline">
              Start a 14-day free trial
            </a>
          </Typography.Text>
        ) : null}
        {step.kind === "sent" ? (
          <div className="flex flex-col gap-3">
            <Alert
              role="status"
              config={{ variant: "subtle", colorScheme: "success", size: "sm" }}
              description="If there's an account for that, a link is on its way. It works once."
            />
            <Button
              type="button"
              variant="link"
              colorScheme="primary"
              size="sm"
              className="self-start"
              onClick={() => setStep({ kind: "password" })}
            >
              Back to sign in
            </Button>
          </div>
        ) : null}
      </div>
      <div className="bg-muted hidden flex-col items-center justify-center gap-2 p-10 lg:flex">
        <Typography.Text as="p" className="m-0 text-lg font-semibold">
          Rigo Accounting
        </Typography.Text>
        <Typography.Text as="p" variant="small" muted className="m-0 max-w-sm text-center">
          Invoices, bills, banking and statements, kept in one ledger.
        </Typography.Text>
      </div>
    </main>
  );
}
